/** Generic in-memory single-flight + short-TTL request de-dup cache.
 *
 * VISTOERA_CANVAS_CAPACITY_CATEGORY_A_SCALING_FIX_AND_REBASELINE_V1 (P0-C
 * / P0-D) — the real-Preview capacity audit
 * (VISTOERA_CANVAS_CAPACITY_AUDIT_AND_CONCURRENCY_BASELINE_V1) measured
 * `getVideoGenConfig` firing 5x and `getVideoGenEngines` firing 3x on a
 * single Canvas mount (one board-level fetch + one independent fallback
 * fetch per `generate_video` node racing it), and the same
 * `getImagePickerItemDetail` id fetched 3x when multiple nodes/materials
 * reference the same asset. Every one of those hits the same
 * capacity-constrained backend DB connection pool (P0-A) — de-duplicating
 * them at the request layer is a pure "fewer requests, same data" win,
 * not a new background-polling cost (spec §31).
 *
 * CACHE_SCOPE: process-lifetime in-memory `Map`, keyed by caller-supplied
 * string (typically `${resourceKind}:${workspaceId}:...`). Not persisted
 * (no localStorage/sessionStorage) — cleared on full page reload exactly
 * like any other JS module singleton.
 *
 * CACHE_INVALIDATION: every entry expires after `ttlMs` (caller-chosen,
 * short — tens of seconds, never "forever"). This is deliberately NOT an
 * indefinite cache specifically so an Admin-side provider/model config
 * edit, or an asset metadata edit, becomes visible again within one TTL
 * window rather than "until the user hard-refreshes" (spec §9).
 *
 * Concurrent callers for the same key while a request is already
 * in-flight share that one Promise (true single-flight) — this is what
 * collapses "board fetch + N racing per-node fallback fetches" into
 * exactly one real HTTP request, and what collapses "M nodes referencing
 * the same asset id" into exactly one request regardless of M. */

interface CacheEntry<T> {
  promise: Promise<T> | null
  value: T | null
  loadedAt: number
}

const registry = new Map<string, CacheEntry<unknown>>()

/** Returns a cached value (if fresh), joins an in-flight request for the
 * same key (if one exists), or starts a new one — never more than one
 * concurrent real request per key. */
export function singleFlightCached<T>(key: string, loader: () => Promise<T>, ttlMs: number): Promise<T> {
  const now = Date.now()
  const existing = registry.get(key) as CacheEntry<T> | undefined

  if (existing?.promise) return existing.promise
  if (existing && existing.value !== null && now - existing.loadedAt < ttlMs) {
    return Promise.resolve(existing.value)
  }

  const entry: CacheEntry<T> = { promise: null, value: existing?.value ?? null, loadedAt: existing?.loadedAt ?? 0 }
  const promise = loader()
    .then((value) => {
      entry.promise = null
      entry.value = value
      entry.loadedAt = Date.now()
      return value
    })
    .catch((err) => {
      // Never cache a failure — the next caller (or the same one, on
      // retry) gets a fresh single-flight attempt instead of a poisoned
      // cache entry.
      entry.promise = null
      throw err
    })
  entry.promise = promise
  registry.set(key, entry as CacheEntry<unknown>)
  return promise
}

/** Test-only / explicit-invalidation escape hatch — not called from any
 * production code path today (TTL expiry is the real invalidation
 * mechanism), but kept so a future "Admin just changed this, force a
 * refetch now" affordance never has to invent its own cache. */
export function invalidateSingleFlightCache(keyPrefix?: string): void {
  if (!keyPrefix) {
    registry.clear()
    return
  }
  for (const key of registry.keys()) {
    if (key.startsWith(keyPrefix)) registry.delete(key)
  }
}
